import React, { useMemo } from "react";
import { 
    PagePreviewBlock as PagePreviewBlockType, 
} from "../../types/optimizely/blocks";
import Link from "../Link/Link";
import PreviewColumn from "./components/PreviewColumn";
import { HeaderType, RenderType } from "./types";
import { getGridClass, getHeaderClassName, getTitleHtml } from "./helpers";
import ArrowRight from "../../assets/arrow-right.svg?react";

interface PagePreviewBlockProps extends PagePreviewBlockType {}

const PagePreviewBlock = ({ 
  title, 
  descriptionText, 
  readMoreLink,
  readMoreLinkText,
  headerType,
  firstColumnArticle, 
  secondColumnArticle,
  thirdColumnArticle,
  fourthColumnArticle,
  firstColumnLinkItemCollection,
  secondColumnLinkItemCollection,
  thirdColumnLinkItemCollection,
  fourthColumnLinkItemCollection,
  renderType,
  showBackground,
  industryPerspectiveMode,
  sectionId,
}: PagePreviewBlockProps) => {
	const isIndustryPerspectiveMode = industryPerspectiveMode?.value;

	const col1 = useMemo(() => ({ article: firstColumnArticle, links: firstColumnLinkItemCollection }), [firstColumnArticle, firstColumnLinkItemCollection]);
	const col2 = useMemo(() => ({ article: secondColumnArticle, links: secondColumnLinkItemCollection }), [secondColumnArticle, secondColumnLinkItemCollection]);
	const col3 = useMemo(() => ({ article: thirdColumnArticle, links: thirdColumnLinkItemCollection }), [thirdColumnArticle, thirdColumnLinkItemCollection]);
	const col4 = useMemo(() => ({ article: fourthColumnArticle, links: fourthColumnLinkItemCollection }), [fourthColumnArticle, fourthColumnLinkItemCollection]);

	const columns = useMemo(() => {
		const cols = [col1, col2, col3, col4];

		return cols.filter(col => {
			const article = col.article?.expandedValue;
			const hasLinks = col.links?.value && col.links.value.length > 0;

			return article || hasLinks;
		});
	}, [col1, col2, col3, col4]);

	const headerClassName = getHeaderClassName(headerType?.value, isIndustryPerspectiveMode);
	const gridClass = useMemo(() => getGridClass(columns.length), [columns]);

	if (columns.length === 0) {
		return null;
	}


  const isHeaderInsideContainer = headerType?.value === HeaderType.Box || headerType?.value === HeaderType.Centered || headerType?.value === HeaderType.Serif || isIndustryPerspectiveMode;

	const header = (
		<div className={`flex justify-between items-end mb-10 ${isHeaderInsideContainer ? 'container' : ''}`}>
			<h2 className={headerClassName} dangerouslySetInnerHTML={{ __html: getTitleHtml(headerType?.value, title.value, isIndustryPerspectiveMode) }} />
      {readMoreLink?.value && ( 
            <Link
            className={`hidden lg:inline-flex max-h-10 ${!isHeaderInsideContainer ? 'mr-8 md:mr-[62px]' : ''}`}
            variant={"link-primary"}
            text={readMoreLinkText.value}
            href={readMoreLink.value}
            />   
          )}
		</div>
	);

	return (
		<section id={sectionId?.value || undefined}>
			{header}
			<div className={"container pb-28"}>
				<p className={"mb-10 text-lg font-normal text-primary"}>{descriptionText.value}</p>

				{/* Mobile view */}
				<div className="lg:hidden">
						{columns.map((col, i) => (
								<div key={`col-mobile-${i}`} className="mb-10 last:mb-0">
										<PreviewColumn
												column={col}
												renderType={renderType?.value ?? RenderType.Column}
												showBackground={!!showBackground?.value}
												columnsCount={columns.length}
										/>
										{col.links?.value && col.links.value.length > 0 && (
												<div className="mt-5">
														<div className="border-t border-primary">
																{col.links.value.map(item => (
																		<a key={item.href} href={item.href} className="flex items-center justify-between py-3 border-b border-primary text-primary text-base font-normal hover:text-primaryBlue group">
																				<span className="w-full">{item.text}</span>
																				<span className="ml-2 group-hover:translate-x-2 transition-all ease-out pr-[10px]">
																						<ArrowRight />
																				</span>
																		</a>
																))}
														</div>
												</div>
										)}
								</div>
						))}
				</div>
				
				{/* Desktop view */}
				<div className={`hidden lg:grid ${gridClass} gap-5`}>
					{columns.map((col, i) => (
						<PreviewColumn
							key={`article-desktop-${i}`}
							column={col}
							renderType={renderType?.value ?? RenderType.Column}
							showBackground={!!showBackground?.value}
							columnsCount={columns.length}
						/>
					))}
					{columns.map((col, i) => {
						const { links } = col;
						if (links?.value && links.value.length > 0) {
							return (
								<div key={`links-desktop-${i}`}>
									<div className="border-t border-primary">
										{links.value.map(item => (
											<a key={item.href} href={item.href} className="flex items-center justify-between py-3 border-b border-primary text-primary text-base font-normal hover:text-primaryBlue group">
												<span className="w-full">{item.text}</span>
												<span className="ml-2 group-hover:translate-x-2 transition-all ease-out pr-[10px]">
													<ArrowRight />
												</span>
											</a>
										))}
									</div>
								</div>
							);
						}
						return <div key={`links-desktop-${i}`} />;
					})}
				</div>
			</div>
		</section>
	);
};

export default PagePreviewBlock;
